import { TLivewireError, UnknownError } from "@/app/exceptions";
import { Livewire } from "@/js/bootstrap/alpinejs";
import { handleErrorAndLog } from "@/js/app/services/errors/handle-js-error";
import { canIgnoreLivewireError, handleLivewireHttpError, isLivewireErrorObject } from "@/js/app/services/errors/livewire-interception";
import is from "../app/support/is";
import { dispatch } from "../app/support/utils";

// Handle global Javascript errors
window.onerror = function (message, source, lineno, colno, error) {
    // If the error is not an object, create one from it
    if (undefined === error) {
        error = new UnknownError(`${message}\n${source}:${lineno}:${colno}` as string);
    }

    handleErrorAndLog(error);
};

// Handle unhandled promise rejections (missed catch)
window.addEventListener("unhandledrejection", function (event: PromiseRejectionEvent) {
    let error: Error;

    if (event.reason instanceof Error) {
        error = event.reason;
    } else if (isLivewireErrorObject(event.reason)) {
        const reason = event.reason as TLivewireError;

        if (canIgnoreLivewireError(reason)) {
            // Not a server error, do not send the error to the error handler, as it is likely a handled error (e.g. validation error)
            dispatch('unhandled-promise', reason);
            return;
        }
        // Server error (5xx) - do not expose details to the user
        error = new UnknownError(
            `Unhandled Rejection (Livewire HTTP Error ${reason.status}): ${reason.body ?? reason.json ?? "No body"}`,
        );
    } else {
        // Rare case - capture detailed information about the non-Error reason
        const reasonType = typeof event.reason;
        let reasonInfo: string;

        try {
            reasonInfo = is.object(event.reason) && event.reason !== null
                ? JSON.stringify(event.reason)
                : String(event.reason);
        } catch {
            // Fallback if stringify fails (circular reference, etc.)
            reasonInfo = String(event.reason);
        }

        // Create an error with all debugging information
        error = new UnknownError(
            `Unhandled Rejection (type: ${reasonType}): ${reasonInfo}`,
        );
    }
    handleErrorAndLog(error);
});


// Covers standard Livewire actions
Livewire.interceptAction(({ onError }) => {
    onError(({ response, body, preventDefault }) => {
        handleLivewireHttpError(response, body, preventDefault);
    });
});
